import React from "react";
import { useTableHooks } from "../../../../atoms/table/useTableHooks";
import { usePaginationHook } from "../../../../molecules/pagination/usePaginationHook";
import { Breadcrumb } from "../../../../atoms/breadcrumb";
import {
    PAGE_NAME,
    PERMISSIONS,
    ROUTE,
} from "../../../../../constants/constants";
import { Input } from "../../../../atoms/input";
import { Table } from "../../../../atoms/table";
import { Divider } from "../../../../atoms/divider";
import { Dropdown } from "../../../../atoms/dropdown";
import { Pagination } from "../../../../molecules/pagination";
import {
    useDeleteSleetProjectMutation,
    useSleetProjectListQuery,
} from "../../../../../store/credentialsManagement/credentialsSlice";
import { hasPermission, pascalToSnakeCase } from "../../../../../utils";
import { useNavigate } from "react-router-dom";
import { Dialog } from "../../../../atoms/dialog";
import { toast } from "react-toastify";

const toolSortFormattedHandler = (sortBy: string) => {
    if (sortBy === "sleetProject") {
        sortBy = "projectName";
    }

    return sortBy;
};

export const SleetProjectList = () => {
    const pageClassName = "p-sleetProjectList";

    const navigate = useNavigate();
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);
    const [deleteId, setDeleteId] = React.useState<string>();

    const [deleteApi, { isLoading: deleteLoading }] =
        useDeleteSleetProjectMutation();
    const tableHeader = [
        {
            title: "Serial",
            dataIndex: "id",
            isSortable: false,
        },
        {
            title: "Name",
            dataIndex: "name",
            isSortable: true,
        },
        {
            title: "Secret Name",
            dataIndex: "secretName",
            isSortable: true,
        },
        {
            title: "Production Feed URL",
            dataIndex: "productionFeedUrl",
            isSortable: true,
        },
        {
            title: "Development Feed URL",
            dataIndex: "developmentFeedUrl",
            isSortable: true,
        },
        {
            title: "Updated At",
            dataIndex: "updatedAt",
            isSortable: true,
        },
    ];
    if (
        hasPermission(PERMISSIONS.SLEET_PROJECT_DELETE.title) ||
        hasPermission(PERMISSIONS.SLEET_PROJECT_CREATE.title)
    ) {
        tableHeader.push({
            title: "Action",
            dataIndex: `action${hasPermission(PERMISSIONS.SLEET_PROJECT_DELETE.title) ? "Delete" : ""
                }${hasPermission(PERMISSIONS.SLEET_PROJECT_CREATE.title) ? "Edit" : ""}`,
            isSortable: false,
        } as { title: string; dataIndex: string; isSortable: boolean });
    }
    const {
        search,
        query,
        pageChangeHandler,
        sortHandler,
        limitChangeHandler,
        searchHandler,
    } = useTableHooks();

    const { data, isLoading } = useSleetProjectListQuery({
        search: query.search,
        limit: query.limit,
        current_page: query.current,
        sort_by: pascalToSnakeCase(toolSortFormattedHandler(query.sortBy)),
        sort_direction: query.sortDirection,
    });

    const {
        totalData,
        handleFirstPage,
        handlePrevPage,
        handleLastPage,
        handleNextPage,
        renderPageNumbers,
    } = usePaginationHook({
        totalNumberOfData: data?.result?.total,
        perPage: query.limit,
        setCurrentPage: pageChangeHandler,
        currentPage: query.current,
        className: "m-pagination",
    });

    const editHandler = (id: string) => {
        navigate(ROUTE.SLEET_PROJECT_LIST + "/edit", { state: { id } });
    };

    const removeHandler = (id: string) => {
        setIsDialogOpen(!isDialogOpen);
        setDeleteId(id);
    };

    const closeDialogHandler = () => {
        setIsDialogOpen(!isDialogOpen);
    };

    const deleteHandler = async () => {
        setIsDialogOpen(!isDialogOpen);
        let response = (await deleteApi(deleteId as string)) as any;

        if (response?.data?.success) {
            toast.success(response?.data?.message);
        } else {
            toast.error(response?.error?.data?.message);
        }
    };

    return (
        <div className={pageClassName}>
            <Breadcrumb />
            <p className={`${pageClassName}__title`}>
                {PAGE_NAME.SLEET_PROJECT_LIST}
            </p>
            <div className={`${pageClassName}__container`}>
                <div className={`${pageClassName}__header`}>
                    <div className={`${pageClassName}__search`}>
                        <Input
                            placeholder="Search"
                            onChange={(e) => {
                                searchHandler(e);
                                handleFirstPage();
                            }}
                            value={search}
                        />
                    </div>
                    {hasPermission(PERMISSIONS.SLEET_PROJECT_CREATE.title) && (
                        <button
                            className={`${pageClassName}__header__button`}
                            onClick={() => navigate(ROUTE.SLEET_PROJECT_LIST + "/create")}
                        >
                            Create Project
                        </button>
                    )}
                </div>
                <div className={`${pageClassName}__table`}>
                    <Table
                        header={tableHeader}
                        data={data?.result?.data ?? []}
                        loading={isLoading || deleteLoading}
                        firstLoad={isLoading}
                        sortHandler={sortHandler}
                        minWidth="1000"
                        startIndex={data?.result?.from}
                        sortBy={query.sortBy}
                        viewURL={ROUTE.SLEET_PROJECT_LIST}
                        cursorPointer
                        editHandler={editHandler}
                        deleteHandler={removeHandler}
                    />
                </div>
                <Divider />
                <div className={`${pageClassName}__pagination`}>
                    <div className={`${pageClassName}__pagination__limit`}>
                        <p>Display</p>
                        <div className={`${pageClassName}__pagination__dropdown`}>
                            <Dropdown
                                options={[
                                    { label: "20", value: 20 },
                                    { label: "50", value: 50 },
                                    { label: "100", value: 100 },
                                ]}
                                selected={{ label: query.limit, value: query.limit }}
                                onClickChange={(e) => {
                                    limitChangeHandler(e.value as number);
                                    handleFirstPage();
                                }}
                                isBordered
                                isIcon
                            />
                        </div>
                    </div>
                    <div className={`${pageClassName}__pagination__number`}>
                        {data?.result?.total ? (
                            <p>
                                Show {data?.result?.from} -{" "}
                                {data?.result?.from + query.limit - 1 < data?.result?.total
                                    ? data?.result?.from + query.limit - 1
                                    : data?.result?.total}{" "}
                                of {data?.result?.total}
                            </p>
                        ) : (
                            <p>Show 00 - 00 of 00</p>
                        )}
                    </div>
                    <div className={`${pageClassName}__pagination__nav`}>
                        {data?.result?.total > query.limit && (
                            <Pagination
                                totalData={totalData}
                                page={query.current}
                                handleFirstPage={handleFirstPage}
                                handlePrevPage={handlePrevPage}
                                handleLastPage={handleLastPage}
                                handleNextPage={handleNextPage}
                                renderPageNumbers={renderPageNumbers}
                            />
                        )}
                    </div>
                </div>
            </div>

            <Dialog
                header="Delete Project"
                body={
                    <div className={`${pageClassName}__dialog`}>
                        <p className={`${pageClassName}__dialog__text`}>
                            Are you sure you want to delete this project?
                        </p>
                        <p className={`${pageClassName}__dialog__text`}>
                            This action cannot be undone.
                        </p>
                    </div>
                }
                isOpen={isDialogOpen}
                onClose={closeDialogHandler}
                successFn={deleteHandler}
                btn="Delete"
                icon="delete"
            />
        </div>
    );
};
